import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import RichTextEditor from "@/components/RichTextEditor";
import { Plus, Trash2, Loader2, Upload, Save, Newspaper, Image as ImageIcon, X, Star, Eye, EyeOff, Instagram, Calendar } from "lucide-react";

function plainToHtml(text) {
  if (!text) return "";
  return text.split(/\n{2,}/).map((p) => `<p>${p.replace(/\n/g, "<br/>")}</p>`).join("");
}

export default function AdminNoticiasTab() {
  const [posts, setPosts] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [limits, setLimits] = useState(null);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [bodyHtml, setBodyHtml] = useState("");
  const [instagramUrl, setInstagramUrl] = useState("");
  const [dateVal, setDateVal] = useState("");
  const coverRef = useRef(null);
  const imgRef = useRef(null);

  useEffect(() => {
    if (selected) {
      setBodyHtml(selected.body_html || plainToHtml(selected.body) || "");
      setInstagramUrl(selected.instagram_url || "");
      setDateVal((selected.date || selected.created_at || "").slice(0, 10));
    }
  }, [selected]);

  const load = useCallback(async () => {
    const [p, al, lim] = await Promise.all([
      api.get("/posts/all").then((x) => x.data),
      api.get("/gallery/albums").then((x) => x.data).catch(() => []),
      api.get("/posts/limits").then((x) => x.data).catch(() => null),
    ]);
    setPosts(p);
    setAlbums(al);
    if (lim) setLimits(lim);
    return p;
  }, []);

  useEffect(() => { load(); }, [load]);

  const atLimit = limits && limits.posts_used >= limits.posts_max;
  const field = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy/30";

  const createPost = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    if (!fd.get("title")) return;
    try {
      const { data } = await api.post("/posts", { title: fd.get("title"), excerpt: fd.get("excerpt") || "" });
      e.target.reset();
      toast.success("Noticia creada");
      await load();
      setSelected(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "No se pudo crear"); }
  };

  const savePost = async (e) => {
    e.preventDefault();
    setSaving(true);
    const fd = new FormData(e.target);
    try {
      const { data } = await api.patch(`/posts/${selected.id}`, {
        title: fd.get("title"), excerpt: fd.get("excerpt") || "", body_html: bodyHtml,
        album_slug: fd.get("album_slug") || null, published: fd.get("published") === "on",
        instagram_url: instagramUrl.trim(), date: dateVal || null,
        highlight: fd.get("highlight") === "on",
      });
      setSelected(data);
      toast.success("Noticia guardada");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "No se pudo guardar"); }
    finally { setSaving(false); }
  };

  const deletePost = async (id) => {
    if (!window.confirm("¿Eliminar esta noticia?")) return;
    await api.delete(`/posts/${id}`);
    if (selected?.id === id) setSelected(null);
    toast.success("Noticia eliminada");
    load();
  };

  const uploadImage = async (file, main) => {
    if (!file || !selected) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file); fd.append("main", main ? "true" : "false");
    try {
      const { data } = await api.post(`/posts/${selected.id}/image`, fd);
      setSelected(data); load();
      toast.success("Imagen subida");
    } catch (err) { toast.error(err?.response?.data?.detail || "No se pudo subir"); }
    finally {
      setUploading(false);
      if (coverRef.current) coverRef.current.value = "";
      if (imgRef.current) imgRef.current.value = "";
    }
  };

  const removeImage = async (url) => {
    const { data } = await api.delete(`/posts/${selected.id}/image`, { params: { url } });
    setSelected(data); load();
  };

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 grid lg:grid-cols-3 gap-6">
      {/* Create + list */}
      <section className="lg:col-span-1">
        <form onSubmit={createPost} className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
          <div className="flex items-center gap-2 text-navy font-bold"><Newspaper className="h-5 w-5 text-gold" /> Nueva noticia</div>
          {limits && (
            <div data-testid="posts-limit" className={`mt-3 text-xs font-semibold rounded-lg px-3 py-2 ${atLimit ? "bg-[#c0392b]/10 text-[#c0392b]" : "bg-[#eef1f7] text-navy"}`}>
              {limits.posts_used} / {limits.posts_max} noticias
            </div>
          )}
          <input name="title" data-testid="post-title-input" placeholder="Título de la noticia" className={`mt-4 ${field}`} />
          <input name="excerpt" data-testid="post-excerpt-input" placeholder="Bajada / resumen breve" className={`mt-3 ${field}`} />
          <button data-testid="post-create-btn" disabled={atLimit} className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-navy text-white font-semibold py-2.5 rounded-lg hover:bg-[#16235a] disabled:opacity-50">
            <Plus className="h-4 w-4" /> Crear noticia
          </button>
        </form>

        <div className="mt-5 space-y-2">
          {posts.map((p) => (
            <button
              key={p.id}
              data-testid={`post-item-${p.slug}`}
              onClick={() => setSelected(p)}
              className={`w-full flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors ${selected?.id === p.id ? "bg-navy text-white border-navy" : "bg-white border-gray-100 hover:border-navy/30"}`}
            >
              <div className="h-11 w-11 rounded-lg overflow-hidden bg-[#eef1f7] shrink-0 flex items-center justify-center">
                {p.cover_url ? <img src={p.cover_url} alt="" className="w-full h-full object-cover" /> : <Newspaper className="h-5 w-5 text-navy/30" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm truncate">{p.title}</div>
                <div className={`text-xs flex items-center gap-1 ${selected?.id === p.id ? "text-gold" : "text-gray-400"}`}>
                  {p.published ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />} {p.published ? "Publicada" : "Borrador"}
                  {p.album_slug && " · álbum"}
                </div>
              </div>
            </button>
          ))}
          {posts.length === 0 && <p className="text-sm text-gray-400 px-1">Todavía no hay noticias.</p>}
        </div>
      </section>

      {/* Editor */}
      <section className="lg:col-span-2">
        {!selected ? (
          <div className="h-full min-h-[300px] bg-white rounded-2xl border border-dashed border-gray-200 flex flex-col items-center justify-center text-gray-400">
            <Newspaper className="h-10 w-10" />
            <p className="mt-3">Seleccioná o creá una noticia para editarla</p>
          </div>
        ) : (
          <form onSubmit={savePost} key={selected.id} data-testid="post-editor" className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-navy font-bold"><Newspaper className="h-5 w-5 text-gold" /> {selected.title}</div>
              <button type="button" onClick={() => deletePost(selected.id)} data-testid="delete-post-btn" className="inline-flex items-center gap-1.5 text-[#c0392b] border border-[#c0392b]/30 px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-[#c0392b]/5">
                <Trash2 className="h-3.5 w-3.5" /> Eliminar
              </button>
            </div>

            {/* Cover + images */}
            <div className="mt-4 flex flex-wrap gap-3 items-start">
              <div className="w-32 shrink-0">
                <div className="aspect-[4/3] rounded-xl overflow-hidden border border-gray-100 bg-[#eef1f7] flex items-center justify-center">
                  {selected.cover_url ? <img src={selected.cover_url} alt={selected.title} className="w-full h-full object-cover" /> : <ImageIcon className="h-8 w-8 text-navy/30" />}
                </div>
                <label className="mt-2 flex items-center justify-center gap-1.5 text-xs font-semibold text-navy border border-gray-200 rounded-lg py-1.5 cursor-pointer hover:border-navy/40">
                  <Star className="h-3.5 w-3.5 text-gold" /> Portada
                  <input ref={coverRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0], true)} disabled={uploading} />
                </label>
              </div>
              <div className="flex-1 min-w-[180px]">
                <div className="text-sm font-semibold text-navy">Imágenes del post ({(selected.images || []).length}/8)</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {(selected.images || []).map((u) => (
                    <div key={u} className="relative h-20 w-20 rounded-lg overflow-hidden border border-gray-100">
                      <img src={u} alt="" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => removeImage(u)} className="absolute top-1 right-1 h-6 w-6 rounded bg-black/50 text-white flex items-center justify-center hover:bg-[#c0392b]"><X className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                  {(selected.images || []).length < 8 && (
                    <label className="h-20 w-20 rounded-lg border border-dashed border-gray-300 flex items-center justify-center cursor-pointer text-gray-400 hover:border-navy/40">
                      {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
                      <input ref={imgRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0], false)} disabled={uploading} />
                    </label>
                  )}
                </div>
              </div>
            </div>

            {/* Fields */}
            <div className="mt-4 grid gap-3">
              <input name="title" defaultValue={selected.title} placeholder="Título" className={field} />
              <input name="excerpt" defaultValue={selected.excerpt || ""} placeholder="Bajada / resumen" className={field} />
              <label className="text-sm">
                <span className="block text-navy font-semibold mb-1 flex items-center gap-1.5"><Calendar className="h-4 w-4 text-gold" /> Fecha de la noticia</span>
                <input type="date" value={dateVal} onChange={(e) => setDateVal(e.target.value)} className={field} data-testid="post-date-input" />
                <span className="mt-1 block text-xs text-gray-400">Las noticias se ordenan por esta fecha (más recientes primero).</span>
              </label>
              <div>
                <span className="block text-sm font-semibold text-navy mb-1.5">Contenido</span>
                <RichTextEditor value={bodyHtml} onChange={setBodyHtml} postId={selected.id} />
              </div>
              <label className="text-sm">
                <span className="block text-navy font-semibold mb-1 flex items-center gap-1.5"><Instagram className="h-4 w-4 text-gold" /> Post de Instagram (opcional)</span>
                <input
                  value={instagramUrl}
                  onChange={(e) => setInstagramUrl(e.target.value)}
                  placeholder="https://www.instagram.com/p/XXXXXXXXX/"
                  className={field}
                  data-testid="post-instagram-input"
                />
                <span className="mt-1 block text-xs text-gray-400">Pegá el enlace de una publicación o reel público. Se mostrará al final de la noticia.</span>
              </label>
              <div className="grid sm:grid-cols-2 gap-3 items-center">
                <label className="text-sm">
                  <span className="block text-navy font-semibold mb-1 flex items-center gap-1.5"><ImageIcon className="h-4 w-4 text-gold" /> Embeber álbum de galería</span>
                  <select name="album_slug" data-testid="post-album-select" defaultValue={selected.album_slug || ""} className={`bg-white ${field}`}>
                    <option value="">Sin álbum</option>
                    {albums.map((a) => <option key={a.slug} value={a.slug}>{a.name} ({a.photo_count} fotos)</option>)}
                  </select>
                </label>
                <label className="inline-flex items-center gap-2 text-sm font-semibold text-navy sm:mt-6">
                  <input type="checkbox" name="published" defaultChecked={selected.published} data-testid="post-published" className="h-4 w-4 accent-navy" />
                  Publicada (visible en el sitio)
                </label>
                <label className="inline-flex items-center gap-2 text-sm font-semibold text-navy">
                  <input type="checkbox" name="highlight" defaultChecked={selected.highlight} data-testid="post-highlight" className="h-4 w-4 accent-navy" />
                  Destacada (aparece primero)
                </label>
              </div>
            </div>
            <button data-testid="save-post-btn" disabled={saving} className="mt-4 inline-flex items-center gap-2 bg-navy text-white font-semibold px-5 py-2.5 rounded-lg hover:bg-[#16235a] disabled:opacity-50">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Guardar noticia
            </button>
          </form>
        )}
      </section>
    </main>
  );
}
